import clsx from 'clsx';
import { Column } from 'react-table';
import Header from '../../../../../../modules/components/datatable/Header';
import SimpleDatatable from '../../../../../../modules/components/datatable/SimpleDatatable';
import UnlabeledBulletColumns from '../../../../../../modules/components/list/UnlabeledBulletColumns';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import ToggleLabelCard from '../../card/ToggleLabelCard';

interface Props {
  data: Array<{
    keyword?: string;
    snippets?: string[];
    isHidden?: boolean;
  }>;
  onToggleVisibility: (keyword: string) => void;
}

export default function CategorizeKeywordTable({ data, onToggleVisibility }: Props) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_CATEGORY_DETAIL.${id}` }, values);
  const [expandedSnippets, setExpandedSnippets] = useState<Set<string>>(new Set());
  const initialSnippetsNum = 3;
  const emDash = '—';

  const columns: Column<Props['data'][number]>[] = [
    {
      Header: () => <Header>{t('TABLE_KEYWORD')}</Header>,
      accessor: (row) => (
        <ToggleLabelCard
          label={row?.keyword}
          isHidden={row?.isHidden}
          onToggle={() => {
            onToggleVisibility(row.keyword);
          }}
          showToggle
        />
      ),
      minWidth: 80,
      id: 'keyword',
    },
    {
      Header: () => <Header>{t('TABLE_SOURCE')}</Header>,
      accessor: (row) => {
        const showAllSnippets = row?.snippets?.length > 0 && expandedSnippets.has(row?.keyword);
        const snippets = showAllSnippets
          ? row?.snippets
          : row?.snippets?.slice(0, initialSnippetsNum);

        return (
          <div className={clsx(row?.isHidden && 'opacity-50')}>
            {row?.snippets?.length > 0 ? (
              <>
                <UnlabeledBulletColumns
                  columnCount={1}
                  data={snippets.map((snippet, sIndex) => {
                    const regex = new RegExp(`(${row?.keyword})`, 'gi');
                    const parts = snippet.split(regex);

                    return (
                      <div key={sIndex}>
                        {parts.map((part, pIndex) =>
                          regex.test(part) ? (
                            <span key={pIndex} className='mark align-middle'>
                              {part}
                            </span>
                          ) : (
                            <span key={pIndex}>{part}</span>
                          )
                        )}
                      </div>
                    );
                  })}
                  shouldBreakText
                />
                {row.snippets?.length > initialSnippetsNum && (
                  <button
                    className='btn btn-link btn-color-muted btn-active-color-primary btn-sm py-1'
                    onClick={(e) => {
                      e.currentTarget.blur();
                      setExpandedSnippets((prev) => {
                        const newSet = new Set(prev);
                        if (showAllSnippets) newSet.delete(row?.keyword);
                        else newSet.add(row?.keyword);
                        return newSet;
                      });
                    }}
                  >
                    {showAllSnippets ? (
                      <>
                        {t('SHOW_LESS')} <i className='bi bi-chevron-compact-up fs-3'></i>
                      </>
                    ) : (
                      <>
                        {t('SHOW_ALL_MORE', { remaining: row.snippets?.length - initialSnippetsNum })}
                        <i className='bi bi-chevron-compact-down fs-3 ms-1' />
                      </>
                    )}
                  </button>
                )}
              </>
            ) : (
              emDash
            )}
          </div>
        );
      },
      minWidth: 200,
      id: 'source',
    },
  ];

  return data?.length > 0 ? (
    <SimpleDatatable data={data} columns={columns} className='m-0' />
  ) : (
    <div className='text-center mt-2'>{t('NO_KEYWORDS_FOUND')}</div>
  );
}
